import React, {
    createContext,
    useState,
    useEffect,
    ReactNode,
    useContext,
  } from "react";
import { IReward } from "../types/atmos";
import { usePremiumCardFlag } from "../context/FeatureContext";
  
  interface PremiumCardData { 
    [key: string]: any;
}
  
  interface PremiumCardContextType {
    premRewards: IReward[];
    isLoading: boolean;
    error: string | null;
  }

  interface PremiumCardProviderProps { 
    children: ReactNode; 
    mpNumber: string;
}
  
  const PremiumCardContext = createContext<PremiumCardContextType | undefined>(undefined);
  
  const PremiumCardProvider: React.FC<PremiumCardProviderProps> = ({
    children, mpNumber
  }) => {
    const [wifiData, setWifiData] = useState<PremiumCardData[]>([]);
    const [loungeData, setLoungeData] = useState<PremiumCardData[]>([]);
    const [premRewards, setPremRewards] = useState<IReward[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const isPremiumCardEnabled = usePremiumCardFlag();
    useEffect(() => {
        if (!isPremiumCardEnabled) {
          setIsLoading(false);
          return; // Don't render anything if the premium card feature is not enabled
        }
        const fetchWifiData = async () => {
          try {
            const response = await fetch(
              process.env.REACT_APP_PREM_WIFI_PASS_URL as string + `?mpNumber=${mpNumber}`,
              {
                method: "GET",
                headers: {
                  "Ocp-Apim-Subscription-Key": process.env.REACT_APP_ATMOS_APIM_KEY as string,
                },
              }
            );
      
            if (!response.ok) {
              throw new Error("Failed to fetch wifi pass data");
            }
      
            const json = await response.json();
            setWifiData(json);
            setIsLoading(false);
          } catch (err: any) {
            setError(err.message || "Unknown error");
            setIsLoading(false);
          }
        };

        const fetchLoungeData = async () => {
          try {
            const response = await fetch(
              process.env.REACT_APP_PREM_LOUNGE_PASS_URL as string + `?mpNumber=${mpNumber}`,
              {
                method: "GET",
                headers: {
                  "Ocp-Apim-Subscription-Key": process.env.REACT_APP_ATMOS_APIM_KEY as string,
                },
              }
            );
      
            if (!response.ok) {
              throw new Error("Failed to fetch lounge pass data");
            }
      
            const json = await response.json();
            setLoungeData(json);
            setIsLoading(false); 
          } catch (err: any) {
            setError(err.message || "Unknown error");
            setIsLoading(false);
          }
        };
      
        fetchWifiData();
        fetchLoungeData();     
      }, [isPremiumCardEnabled]);
     
    useEffect(() => {
      let wifiPasses: IReward[] = [];
      let loungePasses: IReward[] = [];
      let premCardBen: IReward[] = [];

      if (wifiData.length > 0) {
        wifiPasses = wifiData.map((item: any) => ({
          Id: item.id,
          Name: item.name ?? "Wi-Fi pass",
          Status: item.status,
          IssueDate: item.issueDate ?? "",
          ExpirationDate: item.expirationDate ?? "",
          CreationDate: item.creationDateTimeUtc ?? "",
        }));
      }
      
      if (loungeData.length > 0) {
        loungePasses = loungeData.map((item: any) => ({
          Id: item.id,
          Name: item.name ?? "Lounge day pass",
          Status: item.status,
          IssueDate: item.issueDate ?? "",
          ExpirationDate: item.expirationDate ?? "",
          CreationDate: item.creationDateTimeUtc ?? "",
        }));
      }
      
      premCardBen = [...wifiPasses, ...loungePasses];
      setPremRewards(premCardBen);
    }, [wifiData, loungeData]);
        
    return (
      <PremiumCardContext.Provider value={{ premRewards, isLoading, error }}>
        {children}
      </PremiumCardContext.Provider>
    );
  };
  
  // Custom hook
  const usePremiumCard = () => {
    const context = useContext(PremiumCardContext);
    if (context === undefined) {
      throw new Error(
        "usePremiumCard must be used within an PremiumCardProvider"
      );
    }
    return context;  
  };
  
  export { PremiumCardProvider, usePremiumCard };
  